import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import ScrollToTop from "./helpers/scroll-top";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useDispatch } from "react-redux";
import "./translations/i18n";
import urls from "./urls";
import ForgotPassword from "./pages/other/ForgotPassword";
import { Loader } from "./components/loader/Loader";
import { updateCart } from "./redux/slices/cartSlice";
import { notification } from "antd";

import CookieBanner from "./components/banner/CookieBanner";
import RecipesDetail from "./pages/hvo/Recipes/RecipesDetail";

import dayjs from "dayjs";
import "dayjs/locale/nl";
import "dayjs/locale/en";
import dayOfYear from "dayjs/plugin/dayOfYear";
import customParseFormat from "dayjs/plugin/customParseFormat";
import LoyaltyProgramModule from "./components/loyalty-program/LoyaltyProgramModule";
import { ErrorBoundary } from "react-error-boundary";
import { sendExceptionToSlack } from "./services/api/exception";
import ErrorBoundaryFallback from "./components/core/ErrorBoundaryFallback";
import BlogDetail from "./pages/hvo/Blog/BlogDetail";
import BlogOverview from "./pages/hvo/Blog/BlogOverview";
import { Helmet } from "react-helmet";
import FirstVisitSignupModal from "./components/promo/FirstVisitSignupModal";

// Swiper css
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";
import "swiper/css/autoplay";
import { useProducts } from "./components/hooks/firestore/useProducts";
import { useOrderCapacity } from "./components/hooks/firestore/useOrderCapacity";
import { useNotifications } from "./components/hooks/firestore/useNotifications";

dayjs.locale("nl");

dayjs.extend(dayOfYear);
dayjs.extend(customParseFormat);

const Home = lazy(() => import("./pages/home/Home"));
// shop pages
const ShopListStandard = lazy(() => import("./pages/shop/ShopList"));
const CategoryLandingPage = lazy(() => import("./pages/shop/CategoryLanding"));

// product pages
const Product = lazy(() => import("./pages/shop-product/Product"));

// account pages
const MyAccount = lazy(() => import("./pages/account/MyAccount"));
const MyOrders = lazy(() => import("./pages/account/MyOrders"));
const MyFavorites = lazy(() => import("./pages/account/MyFavorites"));
const MyAddresses = lazy(() => import("./pages/account/MyAddresses"));

// other pages
const About = lazy(() => import("./pages/other/About"));
const Contact = lazy(() => import("./pages/other/Contact"));
const Login = lazy(() => import("./pages/other/Login"));
const Register = lazy(() => import("./pages/other/Register"));
const VerifyEmail = lazy(() => import("./pages/other/VerifyEmail"));
const Packaging = lazy(() => import("./pages/hvo/Packaging"));
const Winkel = lazy(() => import("./pages/hvo/Winkel"));
const Grasgevoerd = lazy(() => import("./pages/hvo/Grasgevoerd"));
const Cadeaubon = lazy(() => import("./pages/hvo/Cadeaubon"));

const Cart = lazy(() => import("./pages/other/Cart"));
const Checkout = lazy(() => import("./pages/other/Checkout"));
const OrderConfirmed = lazy(() => import("./pages/other/OrderConfirmed"));
const OrderFailed = lazy(() => import("./pages/other/OrderFailed"));
const OrderProcessing = lazy(() => import("./pages/other/OrderProcessing"));
const PrivacyPolicy = lazy(() => import("./pages/other/PrivacyPolicy"));
const ShippingReturns = lazy(() => import("./pages/other/ShippingReturns"));
const FAQ = lazy(() => import("./pages/other/FAQ"));
const UnsubscribeMails = lazy(() => import("./pages/other/UnsubscribeMails"));

const NotFound = lazy(() => import("./pages/other/NotFound"));

const HalalSlager = lazy(() => import("./pages/hvo/HalalSlager"));
const HalalCertificaten = lazy(() => import("./pages/hvo/HalalCertificates"));
const ReceptenOverview = lazy(
  () => import("./pages/hvo/Recipes/RecipesOverview"),
);

const Context = React.createContext({ name: "Default" });

const App = () => {
  const isProduction = process.env.REACT_APP_ENVIRONMENT === "production";
  const dispatch = useDispatch();
  const { data: products } = useProducts();
  const { data: orderCapacity } = useOrderCapacity();
  const { data: notifications } = useNotifications();
  const [api, contextHolder] = notification.useNotification();
  const contextValue = useMemo(() => ({ name: "notification" }), []);
  const [capacityNotificationsShown, setCapacityNotificationsShown] =
    useState(false);
  const [shownNotificationIds, setShownNotificationIds] = useState(new Set());
  const [lastErrorLog, setLastErrorLog] = useState(null);

  useEffect(() => {
    if (products?.length > 0) {
      dispatch(updateCart(products));
    }
  }, [products]);

  // useEffect(() => {
  //   // if (dayjs().week() === 51 && dayjs().day() < 6) showWeekNotification();
  // }, []);

  useEffect(() => {
    if (
      !capacityNotificationsShown &&
      !!orderCapacity &&
      orderCapacity.length > 0
    ) {
      showCapacityNotifications();
      setCapacityNotificationsShown(true);
    }
  }, [orderCapacity]);

  useEffect(() => {
    if (notifications && notifications.length > 0) {
      showActiveNotifications();
    }
  }, [notifications]);

  const showCapacityNotifications = () => {
    if (!!orderCapacity && orderCapacity.length > 0) {
      const today = dayjs();

      orderCapacity.forEach((capacityItem) => {
        const dayjsDate = dayjs(capacityItem.date, "DD-MM-YYYY");
        const isFutureDate =
          (dayjsDate.dayOfYear() > today.dayOfYear() &&
            dayjsDate.year() === today.year()) ||
          dayjsDate.year() > today.year();

        if (isFutureDate && capacityItem.limits) {
          capacityItem.limits.forEach((limit) => {
            if (
              limit.currentAmount >= limit.limit &&
              limit.notificationTitle &&
              limit.notificationMessage
            ) {
              api.info({
                message: limit.notificationTitle,
                description: limit.notificationMessage,
                duration: 0,
                placement: "bottom",
              });
            }
          });
        }
      });
    }
  };

  const showActiveNotifications = () => {
    const now = dayjs().unix();

    notifications.forEach((notif) => {
      // Check if notification is within active time window
      if (
        notif.startDateTime <= now &&
        notif.endDateTime >= now &&
        !shownNotificationIds.has(notif.id)
      ) {
        api.info({
          message: notif.title,
          description: notif.message,
          duration: 0,
          placement: "bottom",
          onClose: () => {
            // Mark as shown when user closes it
            setShownNotificationIds((prev) => new Set(prev).add(notif.id));
          },
        });
        // Mark as shown immediately after displaying
        setShownNotificationIds((prev) => new Set(prev).add(notif.id));
      }
    });
  };

  const showChristmasNotification = () => {
    notification.info({
      message: "Kerst bestelling: laatste kans 20 december",
      description:
        "De week van 17 t/m 20 december is de laatste kans om een kerst bestelling te laten bezorgen. Afhalen kan wel, op 23 en 24 december.",
      duration: 14,
      placement: "bottom",
    });
  };

  const logError = (error) => {
    const now = Date.now();
    if (
      (process.env.REACT_APP_ENVIRONMENT !== "local" && !lastErrorLog) ||
      now - lastErrorLog > 5000
    ) {
      // 5 seconds cooldown
      const currentPage = window.location.pathname;
      sendExceptionToSlack(
        "Client crash op pagina " + currentPage + ": " + error.message,
      );
      setLastErrorLog(now);
    }
  };

  if (!products) return <Loader />;
  return (
    <>
      {!isProduction && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      )}
      <ErrorBoundary fallback={<ErrorBoundaryFallback />} onError={logError}>
        <Context.Provider value={contextValue}>
          {contextHolder}
        </Context.Provider>
        <Router>
          <ScrollToTop>
            <FirstVisitSignupModal />
            <Suspense fallback={<Loader />}>
              <Routes>
                {/* Homepage */}
                <Route path={urls.home} element={<Home />} />

                {/* Shop pages */}
                <Route path={urls.shop} element={<ShopListStandard />} />
                <Route
                  exact
                  path={urls.shopLandingPage}
                  element={<ShopListStandard />}
                />
                <Route
                  exact
                  path={urls.productsByCategory}
                  element={<ShopListStandard />}
                />

                <Route path={urls.productDetail} element={<Product />} />

                {/* Category landing page */}
                <Route
                  path={urls.categoryLandingPage}
                  element={<CategoryLandingPage />}
                />
                <Route
                  path={urls.subcategoryLandingPage}
                  element={<CategoryLandingPage />}
                />
                {/* Account pages */}
                <Route path={urls.favorites} element={<MyFavorites />} />
                <Route path={urls.my_account} element={<MyAccount />} />
                <Route path={urls.my_orders} element={<MyOrders />} />
                <Route path={urls.my_addresses} element={<MyAddresses />} />

                {/* Other pages */}
                <Route path={urls.about} element={<About />} />
                <Route path={urls.contact} element={<Contact />} />
                <Route path={urls.login} element={<Login />} />
                <Route path={urls.register} element={<Register />} />
                <Route
                  path={urls.forgot_password}
                  element={<ForgotPassword />}
                />
                <Route path={urls.email_verified} element={<VerifyEmail />} />
                <Route path={urls.privacy} element={<PrivacyPolicy />} />
                <Route path={urls.cart} element={<Cart />} />
                <Route path={urls.checkout} element={<Checkout />} />
                <Route
                  path={urls.order_confirmed}
                  element={<OrderConfirmed />}
                />
                <Route path={urls.order_failed} element={<OrderFailed />} />
                <Route
                  path={urls.order_processing + urls.document_id_param}
                  element={<OrderProcessing />}
                />
                <Route path={urls.not_found} element={<NotFound />} />
                <Route
                  path={urls.delivery_returns}
                  element={<ShippingReturns />}
                />
                <Route path={urls.faq} element={<FAQ />} />
                <Route
                  path={urls.unsubscribe_mails}
                  element={<UnsubscribeMails />}
                />
                {/* Landing pages */}
                <Route path={urls.grasgevoerd} element={<Grasgevoerd />} />
                <Route path={urls.packaging} element={<Packaging />} />
                <Route path={urls.halal_slager} element={<HalalSlager />} />
                <Route
                  path={urls.certificates}
                  element={<HalalCertificaten />}
                />
                <Route path={urls.winkel} element={<Winkel />} />
                <Route path={urls.recipes} element={<ReceptenOverview />} />
                <Route path={urls.blog} element={<BlogOverview />} />
                <Route path={urls.cadeaubon} element={<Cadeaubon />} />
                <Route path={urls.recipesDetail} element={<RecipesDetail />} />
                <Route path={urls.blogDetail} element={<BlogDetail />} />
                <Route element={<NotFound />} />
              </Routes>
            </Suspense>
          </ScrollToTop>
          <CookieBanner />
          <LoyaltyProgramModule />
        </Router>
      </ErrorBoundary>
    </>
  );
};

export default App;
